import React, { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MultiSelect } from "@/components/ui/multi-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Filter, FilterX, User, Globe, MessageSquare, Monitor, FileText, DollarSign, Type, Calendar, FileOutput } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FilterValues } from "@/hooks/useKolPostingLinksFilters";
import { supabase } from "@/integrations/supabase/client";

interface KolPostingLinksFiltersProps {
  filters: FilterValues;
  onFilterChange: (field: keyof FilterValues, value: any) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  isLoading?: boolean;
}

// Static predefined options
const STATIC_PIC_OPTIONS = ['Alex', 'Cloris', 'Emre', 'Finn', 'Gemma', 'Greendots', 'Hizkia', 'Jessy', 'Makoto', 'Margo', 'Minh', 'Min Z', 'Molly', 'Nebula', 'Nori', 'Sharding'];
const STATIC_LANGUAGE_OPTIONS = ['CN', 'DE', 'EN', 'ES', 'ID', 'IN', 'IT', 'JP', 'KR', 'PH', 'RU', 'TR', 'VN'];
const STATIC_PLATFORM_OPTIONS = ['Telegram', 'Blog', 'Other', 'Twitter', 'Youtube', 'Tiktok', 'Facebook'];
const STATIC_TYPE_OPTIONS = [
  'Twitter - QT', 'Twitter - Tweet', 'Twitter - RT', 'Twitter - Thread/long post',
  'Telegram - post', 'Youtube - full video', 'Youtube - integration', 
  'Naver - Blog', 'Blogs or News', 'PR', 'AMA', 'Others'
];
const STATIC_EXPENSE_OPTIONS = [
  { value: 'zero', label: '$0' },
  { value: '1-100', label: '$1 - $100' },
  { value: '101-500', label: '$101 - $500' },
  { value: '501-1000', label: '$501 - $1,000' },
  { value: '1000+', label: '$1,000+' }
];

export const KolPostingLinksFilters: React.FC<KolPostingLinksFiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
  isLoading = false
}) => {
  const [contentIdOptions, setContentIdOptions] = useState<string[]>([]);
  const [channelOptions, setChannelOptions] = useState<string[]>([]);

  // Fetch Content ID options from topics_business_units
  useEffect(() => {
    const fetchContentIds = async () => {
      try {
        const { data, error } = await supabase
          .from('topics_business_units')
          .select('content_id')
          .not('content_id', 'is', null)
          .neq('content_id', '')
          .order('content_id');
          
        if (error) throw error;
        
        const uniqueContentIds = [...new Set(data.map(item => item.content_id))].filter(Boolean);
        setContentIdOptions(uniqueContentIds);
      } catch (err) {
        console.error("Error fetching Content ID options:", err);
      }
    };

    fetchContentIds();
  }, []);

  // Fetch unique channel values from kol_tracker
  useEffect(() => {
    const fetchChannelOptions = async () => {
      try {
        const { data, error } = await supabase
          .from('kol_tracker')
          .select('channel')
          .not('channel', 'is', null)
          .neq('channel', '')
          .order('channel');
          
        if (error) throw error;
        
        const uniqueChannels = [...new Set(data.map(item => item.channel))].filter(Boolean);
        setChannelOptions(uniqueChannels);
      } catch (err) {
        console.error("Error fetching channel options:", err);
      }
    };

    fetchChannelOptions();
  }, []);

  const activeFilterCount = React.useMemo(() => {
    return (
      filters.pic.length +
      filters.language.length +
      filters.channel.length +
      filters.platform.length +
      filters.campaign_bu.length +
      filters.expense.length +
      filters.type.length +
      (filters.notes.trim() !== '' ? 1 : 0) +
      (filters.postingDateFrom ? 1 : 0) +
      (filters.postingDateTo ? 1 : 0) +
      (filters.picNullEmpty ? 1 : 0) +
      (filters.languageNullEmpty ? 1 : 0) +
      (filters.channelNullEmpty ? 1 : 0) +
      (filters.platformNullEmpty ? 1 : 0) +
      (filters.campaign_buNullEmpty ? 1 : 0) +
      (filters.expenseNullEmpty ? 1 : 0) +
      (filters.typeNullEmpty ? 1 : 0) +
      (filters.notesNullEmpty ? 1 : 0)
    );
  }, [filters]);

  const filterSections = [
    {
      key: 'pic',
      label: 'PIC',
      icon: User,
      options: STATIC_PIC_OPTIONS.map(pic => ({ value: pic, label: pic })),
      selected: filters.pic,
      nullEmpty: filters.picNullEmpty,
      placeholder: 'Select PICs...'
    },
    {
      key: 'language',
      label: 'Language',
      icon: Globe,
      options: STATIC_LANGUAGE_OPTIONS.map(lang => ({ value: lang, label: lang })),
      selected: filters.language,
      nullEmpty: filters.languageNullEmpty,
      placeholder: 'Select Languages...'
    },
    {
      key: 'channel',
      label: 'Channel',
      icon: MessageSquare,
      options: channelOptions.map(channel => ({ value: channel, label: channel })),
      selected: filters.channel,
      nullEmpty: filters.channelNullEmpty,
      placeholder: 'Select Channels...'
    },
    {
      key: 'platform',
      label: 'Platform',
      icon: Monitor,
      options: STATIC_PLATFORM_OPTIONS.map(platform => ({ value: platform, label: platform })),
      selected: filters.platform,
      nullEmpty: filters.platformNullEmpty,
      placeholder: 'Select Platforms...'
    },
    {
      key: 'type',
      label: 'Type',
      icon: Type,
      options: STATIC_TYPE_OPTIONS.map(type => ({ value: type, label: type })),
      selected: filters.type,
      nullEmpty: filters.typeNullEmpty,
      placeholder: 'Select Types...'
    },
    {
      key: 'campaign_bu',
      label: 'Content ID',
      icon: FileText,
      options: contentIdOptions.map(bu => ({ value: bu, label: bu })),
      selected: filters.campaign_bu,
      nullEmpty: filters.campaign_buNullEmpty,
      placeholder: 'Select Content IDs...'
    }
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="h-8 text-foreground bg-background text-xs transition-colors hover:bg-muted px-3 relative"
          disabled={isLoading}
        >
          <Filter className="h-3.5 w-3.5 mr-1" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs rounded-full">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[560px] p-0 max-h-[700px] overflow-hidden" 
        align="start"
        side="bottom"
        sideOffset={4}
      >
        {/* Header */}
        <div className="p-4 border-b bg-background/95 backdrop-blur sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-semibold text-foreground">Filter Options</h4>
              {activeFilterCount > 0 && (
                <Badge variant="outline" className="text-xs">
                  {activeFilterCount} active
                </Badge>
              )}
            </div>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <FilterX className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>
        
        {/* Scrollable Content */}
        <ScrollArea className="h-[580px]">
          <div className="p-4">
            {/* Posting Date Range Filter */}
            <div className="mb-6 p-4 border rounded-lg bg-muted/20">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium text-foreground">
                  Posting Date Range
                </Label>
                {(filters.postingDateFrom || filters.postingDateTo) && (
                  <Badge variant="secondary" className="h-4 px-1.5 text-xs">
                    {filters.postingDateFrom && filters.postingDateTo ? 'Range' : 'Single'}
                  </Badge>
                )}
              </div>
              
              <DateRangePicker
                from={filters.postingDateFrom}
                to={filters.postingDateTo}
                onFromChange={(date) => onFilterChange('postingDateFrom', date)}
                onToChange={(date) => onFilterChange('postingDateTo', date)}
                className="w-full"
              />
            </div>
            
            <Separator className="mb-4" />
            
            <div className="grid grid-cols-2 gap-6">
              {filterSections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <div key={section.key} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-sm font-medium text-foreground">
                        {section.label}
                      </Label>
                      {section.selected.length > 0 && (
                        <Badge variant="secondary" className="h-4 px-1.5 text-xs">
                          {section.selected.length}
                        </Badge>
                      )}
                    </div>
                    
                    <MultiSelect
                      options={section.options}
                      selected={section.selected}
                      onChange={(value) => onFilterChange(section.key as keyof FilterValues, value)}
                      placeholder={section.placeholder}
                      className="text-xs"
                      maxDisplay={2}
                    />
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`${section.key}-null-empty`}
                        checked={section.nullEmpty}
                        onCheckedChange={(checked) => onFilterChange(`${section.key}NullEmpty` as keyof FilterValues, checked)}
                        className="h-3.5 w-3.5"
                      />
                      <Label 
                        htmlFor={`${section.key}-null-empty`} 
                        className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1"
                      >
                        Include empty values
                      </Label>
                    </div>
                    
                    {/* Add separator between columns but not after the last item */}
                    {index < filterSections.length - 1 && index % 2 === 1 && (
                      <div className="col-span-2 my-2">
                        <Separator />
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Expense Filter */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium text-foreground">
                    Expense
                  </Label>
                  {filters.expense.length > 0 && (
                    <Badge variant="secondary" className="h-4 px-1.5 text-xs">
                      {filters.expense.length}
                    </Badge>
                  )}
                </div>
                
                <MultiSelect
                  options={STATIC_EXPENSE_OPTIONS}
                  selected={filters.expense}
                  onChange={(value) => onFilterChange('expense', value)}
                  placeholder="Select Expense Range..."
                  className="text-xs"
                  maxDisplay={2}
                />
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="expense-null-empty"
                    checked={filters.expenseNullEmpty}
                    onCheckedChange={(checked) => onFilterChange('expenseNullEmpty', checked)}
                    className="h-3.5 w-3.5"
                  />
                  <Label 
                    htmlFor="expense-null-empty" 
                    className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1"
                  >
                    Include null expenses
                  </Label>
                </div>
              </div>
              
              {/* Notes Filter */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <FileOutput className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium text-foreground">
                    Notes
                  </Label>
                  {filters.notes.trim() !== '' && (
                    <Badge variant="secondary" className="h-4 px-1.5 text-xs">
                      Text
                    </Badge>
                  )}
                </div>
                
                <input
                  type="text"
                  placeholder="Search in notes..."
                  value={filters.notes}
                  onChange={(e) => onFilterChange('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-ring bg-background"
                />
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="notes-null-empty"
                    checked={filters.notesNullEmpty}
                    onCheckedChange={(checked) => onFilterChange('notesNullEmpty', checked)}
                    className="h-3.5 w-3.5"
                  />
                  <Label 
                    htmlFor="notes-null-empty" 
                    className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1"
                  >
                    Include empty notes
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
        
        {/* Footer with summary */}
        {hasActiveFilters && (
          <div className="p-3 border-t bg-muted/50">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} applied
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="h-6 px-2 text-xs hover:bg-background"
              >
                Reset all
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
